import React, { useEffect, useState } from "react";
import { Send } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useParams } from "react-router-dom";
import { EmptyConversation, Message } from "@/types";
import axios from "axios";
import { BACKEND_URL } from "@/config";
import { useToken, useUser } from "@/global_state";

export default function ChatPage() {
  const { id } = useParams();
  const [conversation, setConversation] = useState<EmptyConversation | null>(
    null
  );
  const [messages, setMessages] = useState<Message[] | null>([]);
  const [token, setToken] = useToken();
  const [user, setUser] = useUser();

  const fetchConversation = async () => {
    const response = await axios.get(`${BACKEND_URL}/api/conversations/${id}/`);
    setConversation(response.data);
  };

  const fetchMessages = async () => {
    const response = await axios.get(
      `${BACKEND_URL}/api/conversations/${id}/messages/`
    );
    setMessages(response.data.results.reverse());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const input = form.querySelector("input") as HTMLInputElement;
    const content = input.value;
    sendMessage(content).then((response) => {
      input.value = "";
      setMessages((prevMessages) =>
        prevMessages ? [...prevMessages, response] : [response]
      );
    });
  };

  const sendMessage = async (content: string) => {
    const response = await axios.post(
      `${BACKEND_URL}/api/conversations/${id}/messages/`,
      {
        content: content,
      },
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );
    return response.data;
  };

  useEffect(() => {
    if (id && token) {
      fetchConversation();
      fetchMessages();
    }
  }, [id, token]);

  return (
    <div className="flex flex-col h-screen w-screen bg-gray-900 text-gray-100 absolute top-0 pt-12 z-0 pb-20">
      {/* Header */}
      <header className="flex items-center p-4 border-b border-gray-800">
        <Avatar className="h-10 w-10 mr-3">
          <AvatarImage src={conversation?.image} alt="Sarah" />
          <AvatarFallback>{conversation?.name[0]}</AvatarFallback>
        </Avatar>

        <div>
          <h1 className="text-lg font-semibold">{conversation?.name}</h1>
          {/* <p className="text-sm text-gray-400">Online</p> */}
        </div>
      </header>

      {/* Chat messages */}
      <div className="flex-1 overflow-y-auto py-4 px-8 space-y-1">
        {messages?.length === 0 && (
          <div className="h-full flex items-center">
            <span className="text-center w-full">No messages yet</span>
          </div>
        )}
        {messages?.map((message) => (
          <div
            key={message.id}
            className={`flex ${
              message.sender === user?.id ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`max-w-xs md:max-w-md ${
                message.sender === user?.id ? "bg-pink-600" : "bg-gray-700"
              } rounded-lg p-3`}
            >
              <p className="text-sm">{message.content}</p>
              <p className="text-xs text-gray-400 mt-1">{message.time}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Message input */}
      <div className="p-4 border-t border-gray-800">
        <form className="flex space-x-2" onSubmit={handleSubmit}>
          <Input
            type="text"
            placeholder="Type a message..."
            className="flex-1 bg-gray-800 border-gray-700 text-gray-100 focus:ring-pink-600"
            required
          />
          <Button
            type="submit"
            size="icon"
            className="bg-pink-600 hover:bg-pink-700"
          >
            <Send className="h-4 w-4" />
            <span className="sr-only">Send message</span>
          </Button>
        </form>
      </div>
    </div>
  );
}
